import { PrefixOptions, SuffixOptions, ValueType, YesOrNo, parseFormValue } from './formFieldConstants';
import { calculateAge } from 'utilities/common';
import { ZERO_GUID } from 'modules/enumerations/enumerationIds';
import React from 'react';
import { Checkbox } from '@mui/material';

//TODO - This needs to be done on the backend
const maskSSN = ssn => {
  if (!ssn) return 'N/A';
  const digitsOnly = ssn.replace(/\D/g, '');
  if (digitsOnly.length !== 9) return 'Invalid SSN';
  return `XXX-XX-${digitsOnly.slice(-4)}`;
};

export const createFamilyColumns = enumerations => {
  // ZERO_GUID is seeded as an active, blank relationship type. The API accepts it, so it isn't offered and counts as unset.
  const relationshipOptions = (enumerations?.familyRelationshipTypes || []).filter(
    s => s.isActive === true && s.id !== ZERO_GUID,
  );

  return [
    {
      accessorKey: 'prefix',
      header: 'Prefix',
      type: 'dropdown',
      required: false,
      options: PrefixOptions,
      hideInTable: true,
      displayInHeaderRow: true
    },
    { accessorKey: 'firstName', header: 'First Name', type: 'text', required: true, size: 65, displayInHeaderRow: true },
    { accessorKey: 'middleName', header: 'Middle', type: 'text', required: false, size: 65, hideInTable: true, displayInHeaderRow: true },
    { accessorKey: 'lastName', header: 'Last Name', type: 'text', required: true, size: 65, displayInHeaderRow: true },
    {
      accessorKey: 'suffix',
      header: 'Suffix',
      type: 'dropdown',
      required: false,
      options: SuffixOptions,
      hideInTable: true,
      displayInHeaderRow: true
    },
    {
      accessorKey: 'familyRelationshipTypeId',
      header: 'Relationship',
      type: 'dropdown-v2',
      // The API binds this to a non-nullable Guid, so a family member cannot be saved without one.
      required: true,
      options: relationshipOptions,
      size: 65,
      Cell: ({ cell }) => <div>{relationshipOptions.find(at => at.id === cell.row.original.familyRelationshipTypeId)?.title}</div>
    },
    {
      accessorKey: 'ssn',
      header: 'Social Sec. Number',
      type: 'text',
      required: false,
      hideInTable: true,
      Cell: ({ cell }) => maskSSN(cell.getValue())
    },
    {
      accessorKey: 'dateOfBirth',
      header: 'Birthday',
      type: 'date',
      required: false,
      hideInTable: true,
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-')
    },
    //TODO - Calculate this
    {
      accessorKey: 'age',
      header: 'Age',
      type: 'number',
      required: false,
      hideInModel: true,
      size: 65
    },
    { accessorKey: 'primaryPhoneNumber', header: 'Primary Phone Number', type: 'text', required: false, hideInTable: true },
    { accessorKey: 'personalEmailAddress', header: 'Primary Email Address', type: 'text', required: false, hideInTable: true },
    {
      accessorKey: 'highSchoolGraduationDate',
      header: 'High School GradDate',
      type: 'date',
      required: false,
      hideInTable: true,
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-')
    },
    {
      accessorKey: 'isOnAssignmentWithFamily',
      header: 'On Assignment With Family',
      type: 'checkbox',
      required: false,
      hideInTable: true
    },
    {
      accessorKey: 'isPrimary',
      header: 'Is Primary',
      type: 'checkbox',
      required: false,
      Cell: ({ cell }) => <Checkbox name="isPrimary" label="Active" checked={cell.row.original.isPrimary} disabled={true} />
    },
    { accessorKey: 'languages', header: 'Languages', type: 'notes', required: false, hideInTable: true },
    { accessorKey: 'specialNeeds', header: 'Special Needs', type: 'notes', required: false, size: 65 },
    { accessorKey: 'comments', header: 'Comments', type: 'notes', required: false, hideInTable: true }
  ];
};

export const createFamilyRows = formDataList => {
  if (!formDataList || !Array.isArray(formDataList)) {
    return [];
  }

  return formDataList.map(data => mapFamilyRow(data));
};

// The API stores typed phone numbers (mobile/home/work); 'Primary Phone Number' shows and edits the first one that has a value.
const PHONE_KEYS = ['mobilePhoneNumber', 'homePhoneNumber', 'workPhoneNumber'];
const resolvePrimaryPhoneKey = data => PHONE_KEYS.find(key => data?.[key]) || PHONE_KEYS[0];

export const mapFamilyRow = data => {
  const resolvedPersonalEmail = data?.personalEmailAddress || data?.primaryEmailAddress || '';

  return {
    ...data,
    // Blank relationships load as unset so editing the member requires choosing one.
    familyRelationshipTypeId: data?.familyRelationshipTypeId === ZERO_GUID ? '' : data?.familyRelationshipTypeId,
    personalEmailAddress: resolvedPersonalEmail,
    // Keep the legacy key in sync for components still reading it.
    primaryEmailAddress: resolvedPersonalEmail,
    primaryPhoneNumber: data?.[resolvePrimaryPhoneKey(data)] || '',
    prefix: !data?.prefix || data?.prefix === '' ? PrefixOptions[0] : data?.prefix,
    suffix: !data?.suffix || data?.suffix === '' ? SuffixOptions[0] : data?.suffix,

    onAssignmentWithFamily: data?.onAssignmentWithFamily ? YesOrNo[0] : YesOrNo[1],
    notPartOfFinalMove: data?.notPartOfFinalMove ? YesOrNo[0] : YesOrNo[1],
    ssn: maskSSN(data.ssn),
    birthDate: parseFormValue(data.birthDate, ValueType.DateTimeType),
    age: calculateAge(data.birthDate),
    highSchoolGraduationDate: parseFormValue(data.highSchoolGraduationDate, ValueType.DateTimeType)
  };
};

export const getNameOfDeleteRow = row => {
  return `${row.getValue('firstName') ?? ''} ${row.getValue('lastName') ?? ''}`;
};

export const createFamilyPostRequest = (formData, authorizationId) => {
  const request = { ...formData };
  request.authorizationId = authorizationId;
  request.fileId = Number(authorizationId);

  parseFamilyFormValues(request, formData);
  return request;
};

export const createFamilyPutRequest = (originalFormData, newFormData, authorizationId) => {
  // The API overwrites every column on update, so start from the stored member and apply the form on top. Fields the
  // form doesn't show (gender, the other phone numbers, work email, part of final move) are then sent back unchanged.
  const request = { ...originalFormData, ...newFormData };
  request.authorizationId = authorizationId;
  request.familyMemberId = originalFormData.familyMemberId;
  request.fileId = originalFormData.fileId;
  request.rowVersion = originalFormData.rowVersion;

  // The edit form shows the masked SSN; keep the stored one unless the user entered a different value.
  if (request.ssn === maskSSN(originalFormData.ssn)) {
    request.ssn = originalFormData.ssn || '';
  }

  parseFamilyFormValues(request, newFormData, originalFormData);
  return request;
};

// storedMember is the member as last loaded (edits only); a new member's phone number is saved as mobile.
const parseFamilyFormValues = (currentRequest, newRequestValues, storedMember) => {
  const resolvedPersonalEmail = newRequestValues?.personalEmailAddress || newRequestValues?.primaryEmailAddress || '';

  currentRequest.isOnAssignmentWithFamily = newRequestValues.isOnAssignmentWithFamily === true;
  currentRequest.dateOfBirth = parseFormValue(newRequestValues.dateOfBirth, ValueType.DateTimeType);
  currentRequest.age = calculateAge(newRequestValues.dateOfBirth);
  currentRequest.highSchoolGraduationDate = parseFormValue(newRequestValues.highSchoolGraduationDate, ValueType.DateTimeType);
  currentRequest.personalEmailAddress = resolvedPersonalEmail;
  currentRequest.primaryEmailAddress = resolvedPersonalEmail;
  if (newRequestValues?.primaryPhoneNumber !== undefined) {
    // Save to the same typed slot (mobile/home/work) the number was shown from, so a work number stays a work number.
    currentRequest[resolvePrimaryPhoneKey(storedMember)] = newRequestValues.primaryPhoneNumber ?? '';
  }
  currentRequest.isPrimary = newRequestValues.isPrimary === true;
  currentRequest.addedBy = 1;
  currentRequest.modifiedBy = 1;
};
